import {
  DatePicker as MUIDatePicker,
  type DatePickerProps as MUIDatePickerProps,
} from '@mui/x-date-pickers/DatePicker';
import { PickerValidDate } from '@mui/x-date-pickers/models';

import { blue, red, slate } from '@/lib/colours';

import { FormHelperText, type TextFieldProps } from '@/primitives';
import useDateFormatFromOrgSettings from '@/hooks/useDateFormatFromOrgSettings';

export interface DatePickerProps<TDate extends PickerValidDate>
  extends MUIDatePickerProps<TDate> {
  error?: TextFieldProps['error'];
  helperText?: TextFieldProps['helperText'];
  required?: TextFieldProps['required'];
  variant?: TextFieldProps['variant'];
}

export function DatePicker<TDate extends PickerValidDate>({
  error,
  helperText,
  ...props
}: DatePickerProps<TDate>) {
  const dateFormat = useDateFormatFromOrgSettings();

  if (props.variant === 'filled') {
    return (
      <>
        <MUIDatePicker
          format={dateFormat}
          {...props}
          slotProps={{
            ...props.slotProps,
            textField: {
              ...props.slotProps?.textField,
              error,
              required: props.required,
              variant: props.variant,
              InputLabelProps: {
                shrink: true,
                sx: {
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: '500',
                  lineHeight: '20px',
                  letterSpacing: '0.25px',
                  paddingTop: '3px',
                  paddingLeft: '1px',
                },
              },
              InputProps: {
                disableUnderline: true,
                sx: {
                  backgroundColor: slate[50],
                  borderRadius: '8px',
                  border: `1px solid ${slate[300]}`,
                  ':focus-within': {
                    borderColor: blue[600],
                  },
                  ':has(input[aria-invalid="true"])': {
                    borderColor: red[600],
                  },
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: '400',
                  lineHeight: '20px',
                  letterSpacing: '0.25px',
                  '&.MuiFilledInput-root': {
                    backgroundColor: 'transparent',
                  },
                  '.Mui-disabled': {
                    borderRadius: '8px',
                    fontStyle: 'italic',
                  },
                },
              },
            },
          }}
        />
        {error && helperText ? (
          <FormHelperText error={error} sx={{ marginLeft: '14px' }}>
            {helperText}
          </FormHelperText>
        ) : null}
      </>
    );
  }

  return (
    <>
      <MUIDatePicker
        format={dateFormat}
        {...props}
        slotProps={{
          textField: {
            required: props.required,
            variant: props.variant,
          },
        }}
      />
      {error && helperText ? (
        <FormHelperText error={error} sx={{ marginLeft: '14px' }}>
          {helperText}
        </FormHelperText>
      ) : null}
    </>
  );
}
